import React, { useState } from "react";
import { baseUrl, postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts";
import {
    PublishStatus,
    PublishSaveRequestData,
    CrossAppUnpublishRequestData,
    PublishSaveResponseData,
    KmsTypeCategoryMembership,
} from "@kaltura/mediaspace-shared-types";
import { PrivateModal } from "./private-modal/PrivateModal";
import { UnlistedModal } from "./unlisted-modal/UnlistedModal";
import { PublishedModalContainer } from "./published-modal/PublishedModalContainer";
import { ExternalUnpublishConfirmModal } from "./external-unpublish-confirm-modal/ExternalUnpublishConfirmModal";
import { SaveType } from "../../utils";
import { ToastMessages } from "@kaltura/mediaspace-shared-ui";

export interface ModalManagerProps {
    entryIds: string[];
    publishStatus?: PublishStatus;
    targetStatus: PublishStatus;
    crossApplicationUnpublishing: boolean;
    onSubmit: (result: PublishSaveResponseData) => void;
    onClose: () => void;
    isBulk: boolean;
    disabledCategoryMembershipIds?: KmsTypeCategoryMembership[];
}

/**
 * Publish Menu Item selection Modal Manager - select the correct modal by publish state,
 * and communicate with KMS
 */
export function ModalManager(props: ModalManagerProps) {
    const {
        entryIds,
        publishStatus,
        targetStatus,
        crossApplicationUnpublishing,
        onClose,
        onSubmit,
        isBulk,
        disabledCategoryMembershipIds,
    } = props;

    const [showExternalConfirm, setShowExternalConfirm] = useState(false);

    const { showToast } = useToastsContext();

    // submit save or crossApplicationUnpublish to kms
    const handleSubmit = async (
        data: PublishSaveRequestData | CrossAppUnpublishRequestData,
        saveType = SaveType.save
    ) => {
        // set save url by save type
        // save - private/unlisted/publish
        // or cross-application-unpublish
        const url =
            saveType === SaveType.save
                ? `${baseUrl}/publishmedia/media/save/`
                : `${baseUrl}/publishmedia/media/cross-application-unpublish/`;
        // send data to kms
        try {
            const result: PublishSaveResponseData = await postKmsData(url, data);
            // show toast
            if (result.messages) {
                for (const message of result.messages) {
                    showToast({ severity: "success", message: message });
                }
            }
            if (result.errorMessages && result.errorMessages.length > 0) {
                showToast({
                    message: (
                        <ToastMessages
                            title={translate("Could not publish the following media:")}
                            messages={result.errorMessages}
                        />
                    ),
                    severity: "error",
                });
            }
            // update the menu state
            onSubmit(result);
        }
        catch (e) {
            showToast({ severity: "error", message: translate("Error updating media") });
            // close modal
            onClose();
        }
    };

    const handleUnpublishExternal = () => {
        setShowExternalConfirm(true);
    };

    switch (targetStatus) {
        case PublishStatus.UNLISTED:
            return (
                <UnlistedModal
                    entryIds={entryIds}
                    publishStatus={publishStatus}
                    onSubmit={handleSubmit}
                    onClose={onClose}
                    isBulk={isBulk}
                />
            );
        case PublishStatus.PRIVATE:
            return (
                <PrivateModal
                    entryIds={entryIds}
                    publishStatus={publishStatus}
                    onSubmit={handleSubmit}
                    onClose={onClose}
                    isBulk={isBulk}
                />
            );
        case PublishStatus.PUBLISHED:
            if (!showExternalConfirm) {
                return (
                    <PublishedModalContainer
                        entryIds={entryIds}
                        publishStatus={publishStatus}
                        crossApplicationUnpublishing={crossApplicationUnpublishing}
                        onUnpublishExternal={handleUnpublishExternal}
                        onSubmit={handleSubmit}
                        onClose={onClose}
                        isBulk={isBulk}
                        disabledCategoryMembershipIds={disabledCategoryMembershipIds}
                    />
                );
            }
            else {
                return (
                    <ExternalUnpublishConfirmModal
                        entryIds={entryIds}
                        publishStatus={publishStatus}
                        onSubmit={handleSubmit}
                        onClose={onClose}
                    />
                );
            }
        default:
            return null;
    }
}
